import React, { useState, useCallback, useEffect } from 'react';
import { usePromptTemplateService, PromptTemplateType } from '../services/promptTemplateService';
import {
  Paper,
  Stack,
  Typography,
  Divider,
  Button,
  Box,
  CircularProgress,
  Drawer,
  IconButton
} from '@mui/material';
import {
  Info as InfoIcon,
  Business as BusinessIcon,
  Flag as FlagIcon,
  Hail as HailIcon,
  School as SchoolIcon,
  Article as ArticleIcon,
  Close as CloseIcon,
  Padding
} from '@mui/icons-material';
import InlineEditField from './InlineEditField';
import { useVoiceService, AzureVoice } from '../services/voiceService';
// Import the SDK and a speech token service
import * as SpeechSDK from 'microsoft-cognitiveservices-speech-sdk';
import { useSpeechTokenService } from '../services/speechTokenService';

export interface RoleplayDetailsValues {
  title: string;
  description: string;
  meetingSummary: string;
  duration: string;
  promptTemplateId?: number;
  courseId?: number;
  goals: string[];
  personaName: string;
  personaRole: string;
  personaBehaviors: string;
  personaBio: string;
  voiceName: string;
  voiceGender: string;
  companyName: string;
  companyIndustry: string;
  companySize: string;
  companyLocations: string;
  companyTechProfile: string;
  companyChallenges: string;
  scenarioMeetingType: string;
  scenarioCustomerType: string;
  scenarioHistory: string;
  scenarioInitiatives: string;
  scenarioChallenges: string;
  scenarioConstraints: string;
}

interface RoleplayDetailsTabProps {
  mode: 'create' | 'edit';
  values: RoleplayDetailsValues;
  onChange: (values: RoleplayDetailsValues) => void;
  editable: boolean;
  saving?: boolean;
  errors?: Record<string, string>;
}

const durationOptions = [5,10,15,20,25,30,35,40,45];

const initialVoices: AzureVoice[] = [
  {
    Name: 'Serena',
    ShortName: 'en-US-Serena:DragonHDLatestNeural',
    DisplayName: 'Serena',
    LocalName: 'Serena',
    Locale: 'en-US',
    Gender: 'Female',
    SampleRateHertz: '48000',
    VoiceType: 'Neural',
    Status: 'Active',
  },
  {
    Name: 'Steffan',
    ShortName: 'en-US-Steffan:DragonHDLatestNeural',
    DisplayName: 'Steffan',
    LocalName: 'Steffan',
    Locale: 'en-US',
    Gender: 'Male',
    SampleRateHertz: '48000',
    VoiceType: 'Neural',
    Status: 'Active',
  },
];

const sectionFieldExplanations = {
    roleplay: {
      title: "Example - Why SHI for Datacenter",
      description: "Establish credibility, understand challenges, explore how SHI can support.",
      meetingSummary: "Mark Reynolds, Director of Infrastructure at Oxford Enterprises, is responsible for the technology strategy of a 15,000-user financial services company headquartered in New York, with 12 locations nationwide. With 15 years of experience in enterprise IT and a recent promotion after seven years at Oxford, Mark brings deep expertise in large-scale infrastructure management and hybrid cloud adoption. The company is experiencing rapid growth in user activity and data requirements, and leadership is pushing for cost reduction and greater operational efficiency. This is the first meeting between Mark and SHI focused on infrastructure solutions; while Oxford is an existing SHI customer in procurement and end user devices, there has been no previous engagement with the infrastructure team. The conversation is an opportunity to establish credibility, understand Mark’s current challenges, and explore how SHI can support Oxford’s modernization efforts.",
      duration: "20 minutes"
    },
    goals: {
      goals: `Use a customer-centric approach and question-based selling to uncover data center opportunities.
 
Profile Oxford Enterprises’ infrastructure and uncovering current pain points or inefficiencies.
 
Understand customer challenges by articulating how SHI provides value and speaks to their needs.
 
Demonstrate confidence in discussing SHI’s data center offerings.
 
Secure a follow-up meeting.`
    },
    persona: {
      name: "Mark Reynolds",
      roleOrJobTitle: "Director of Infrastructure",
      behavioralTendencies: "15 years of experience in IT infrastructure management. Specializes in large-scale enterprise environments, hybrid cloud adoption, and virtualization. With Oxford Enterprises for 7 years. Promoted from Senior Infrastructure Engineer to Director of Infrastructure 8 months ago.",
      backgroundAndBio: "Collaborative and open to discussing solutions. Deliberate in decision-making. Expects thoughtful, specific questions from sellers. Reserved and somewhat guarded with information on new areas of business. Only provides information as would be natural for a first-time topic with a new vendor contact. Neutral, businesslike, and slightly skeptical in initial tone. Provides limited information until relevance or value is established. Mirrors specificity of seller’s questions: brief responses to closed questions, elaborates on open-ended questions. Focuses on solutions that deliver measurable outcomes aligned with his priorities. Deliberate about agreeing to next steps; only commits if clear value is demonstrated."
    },
    customer: {
      companyName: "Oxford Enterprises",
      companyIndustry: "Financial Services",
      companySize: "15,000 users",
      companyLocations: "12 locations across the US, HQ in New York",
      companyTechProfile: "Primary vendors: HPE (servers), NetApp (storage), VMware (virtualization/hybrid cloud). Backup solutions: Legacy systems with Veeam; outdated hardware/software causing inefficiencies. Cloud usage: Limited, mainly exploring Azure for hybrid cloud. Hybrid exploration: Actively evaluating Azure Arc and VMware Cloud.",
      companyChallenges: "Rapid growth in user activity and data requirements. Leadership is focused on reducing costs while improving operational efficiency."
    },
    scenario: {
      typeOfType: "First conversation between Mark and SHI about infrastructure/data center solutions.",
      customerType: "Existing (SHI works with Oxford in other areas (procurement and devices) but infrastructure is a new area of business).",
      relevantHistory: "No prior engagement with SHI’s infrastructure or data center team and unfamiliar with offerings in this area.",
      keyInitiatives: "Transitioning from legacy hardware/software to hybrid cloud solutions for scalability and distributed operations. Implementing advanced monitoring for real-time infrastructure insights. Upgrading backup and recovery processes to meet compliance and minimize downtime.",
      painPointsAndChallenges: "Transitioning from legacy hardware/software to hybrid cloud solutions for scalability and distributed operations. Implementing advanced monitoring for real-time infrastructure insights. Upgrading backup and recovery processes to meet compliance and minimize downtime.",
      specialConstraintsAndTriggers: "Budget-conscious decision-making. High compliance standards as a financial services company. Leadership is wary of new vendors and expects measurable outcomes. Behave as if he is meeting SHI's infrastructure team for the first time and knows little about their capabilities beyond procurement and device licensing."
    }
  } as const;

  type SectionKey = keyof typeof sectionFieldExplanations;
  type RoleplayFieldKey = keyof typeof sectionFieldExplanations['roleplay'];
  type GoalsFieldKey = keyof typeof sectionFieldExplanations['goals'];
  type PersonaFieldKey = keyof typeof sectionFieldExplanations['persona'];
  type CustomerFieldKey = keyof typeof sectionFieldExplanations['customer'];
  type ScenarioFieldKey = keyof typeof sectionFieldExplanations['scenario'];

  type SectionFieldTuple =
    | ['roleplay', RoleplayFieldKey]
    | ['goals', GoalsFieldKey]
    | ['persona', PersonaFieldKey]
    | ['customer', CustomerFieldKey]
    | ['scenario', ScenarioFieldKey];

  function camelToTitle(str: string): string {
  return str
    // Insert a space before all caps
    .replace(/([A-Z])/g, ' $1')
    // Capitalize the first letter
    .replace(/^./, s => s.toUpperCase())
    .trim();
}

const RoleplayDetailsTab: React.FC<RoleplayDetailsTabProps> = ({
  values,
  onChange,
  editable,
  errors = {}
}) => {
  const { fetchVoices } = useVoiceService();
  const [voices, setVoices] = useState<AzureVoice[]>(initialVoices);
  const [moreVoicesLoaded, setMoreVoicesLoaded] = useState(false);
  const [loadingVoices, setLoadingVoices] = useState(false);
  const [sidecarSection, setSidecarSection] = useState<SectionKey | null>(null);
  const [sidecarSectionField, setSidecarSectionField] = useState<SectionFieldTuple | null>(null);



  // Speech token hook
  const { fetchSpeechToken } = useSpeechTokenService();
  const [speechCredentials, setSpeechCredentials] = useState<{ token: string; region: string } | null>(null);
  const [testingVoice, setTestingVoice] = useState(false);

  // Fetch speech credentials only when testing voice for the first time
  const getSpeechCredentials = useCallback(async () => {
    if (speechCredentials) return speechCredentials;
    const creds = await fetchSpeechToken();
    setSpeechCredentials(creds);
    return creds;
  }, [fetchSpeechToken, speechCredentials]);

  // Test Voice Button Handler
  const handleTestVoice = useCallback(async () => {
    if (!values.voiceName) return;
    setTestingVoice(true);
    try {
      const creds = await getSpeechCredentials();
      const speechConfig = SpeechSDK.SpeechConfig.fromAuthorizationToken(creds.token, creds.region);
      speechConfig.speechSynthesisVoiceName = values.voiceName;
      speechConfig.speechSynthesisOutputFormat =
        SpeechSDK.SpeechSynthesisOutputFormat.Audio16Khz32KBitRateMonoMp3;
      const synthesizer = new SpeechSDK.SpeechSynthesizer(speechConfig);
      synthesizer.speakTextAsync(
        "This is a test of the selected voice.",
        () => {
          synthesizer.close();
          setTestingVoice(false);
        },
        error => {
          console.error("Test voice error:", error);
          synthesizer.close();
          setTestingVoice(false);
        }
      );
    } catch (e) {
      console.error("Test voice exception:", e);
      setTestingVoice(false);
    }
  }, [getSpeechCredentials, values.voiceName]);

  const loadMoreVoices = () => {
    setLoadingVoices(true);
    fetchVoices()
      .then(data => {
        setVoices(data);
        setMoreVoicesLoaded(true);
        setLoadingVoices(false);
      })
      .catch(() => setLoadingVoices(false));
  };

  // Always show the selected voice in options
  let voiceOptions = moreVoicesLoaded ? voices : initialVoices;
  if (
    values.voiceName &&
    !voiceOptions.find(v => v.ShortName === values.voiceName)
  ) {
    const selectedVoice =
      voices.find(v => v.ShortName === values.voiceName) ||
      initialVoices.find(v => v.ShortName === values.voiceName);

    if (selectedVoice) {
      voiceOptions = [...voiceOptions, selectedVoice];
    }
  }

  const handleFieldChange = (field: keyof RoleplayDetailsValues, value: string) => {
    onChange({ ...values, [field]: value });
  };

  // Prompt Template Options
  const { fetchPromptTemplates } = usePromptTemplateService();
  const [promptTemplateOptions, setPromptTemplateOptions] = useState<{ id: number; name: string }[]>([]);
  useEffect(() => {
    fetchPromptTemplates()
      .then((templates: PromptTemplateType[]) => {
        setPromptTemplateOptions(
          templates.map(t => ({ id: t.id, name: t.title || `Prompt Template ${t.id}` }))
        );
      })
      .catch(() => setPromptTemplateOptions([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleGoalChange = (idx: number, value: string) => {
    const newGoals = [...(values.goals || [])];
    newGoals[idx] = value;
    onChange({ ...values, goals: newGoals });
  };
  const handleAddGoal = () => {
    onChange({ ...values, goals: [...(values.goals || []), ''] });
  };

  return (
    <Stack spacing={4}>
      {/* Scenario Summary Section */}
      <Paper elevation={3} sx={{ p: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2, width: '100%' }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <SchoolIcon color="primary" />
            <Typography variant="h6" fontWeight={700}>
              Roleplay Details
            </Typography>
          </Stack>
          <InfoIcon
            sx={{ cursor: 'pointer' }}
            onClick={() => {
              setSidecarSection('roleplay');
              setSidecarSectionField(null);
            }}
          />
        </Stack>
        <Divider sx={{ mb: 2 }} />
        <InlineEditField
          label="Title"
          value={values.title}
          onSave={val => handleFieldChange('title', val)}
          alwaysEditing={editable}
          required
          error={!!errors.title}
          helperText={errors.title}
        />
        <InlineEditField
          label="Description"
          value={values.description}
          onSave={val => handleFieldChange('description', val)}
          alwaysEditing={editable}
          multiline
          required
          error={!!errors.description}
          helperText={errors.description}
        />
        <InlineEditField
          label="Meeting Summary"
          value={values.meetingSummary}
          onSave={val => handleFieldChange('meetingSummary', val)}
          alwaysEditing={editable}
          multiline
          required
          error={!!errors.meetingSummary}
          helperText={errors.meetingSummary}
        />
        <InlineEditField
          label="Duration"
          value={values.duration}
          onSave={val => handleFieldChange('duration', val)}
          alwaysEditing={editable}
          required
          select
          options={[
            { value: '', label: 'Select duration' },
            ...durationOptions.map(dur => ({ value: String(dur), label: `${dur} minutes` }))
          ]}
          error={!!errors.duration}
          helperText={errors.duration}
        />
        <InlineEditField
          label="Prompt Template"
          value={
            typeof values.promptTemplateId === 'number'
              ? String(values.promptTemplateId)
              : values.promptTemplateId || ''
          }
          onSave={val =>
            handleFieldChange(
              'promptTemplateId',
              val === '' ? '' : val
            )
          }
          alwaysEditing={editable}
          required
          select
          options={[
            { value: '', label: 'Select prompt template' },
            ...promptTemplateOptions.map(template => ({ value: String(template.id), label: template.name }))
          ]}
          error={!!errors.promptTemplateId}
          helperText={errors.promptTemplateId}
        />
        <InlineEditField
          label="Course ID"
          value={values.courseId !== undefined ? String(values.courseId) : ""}
          onSave={val => handleFieldChange('courseId', val)}
          alwaysEditing={editable}
          multiline
          required
          error={!!errors.courseId}
          helperText={errors.courseId}
        />
      </Paper>

      {/* Goals Section */}
      <Paper elevation={2} sx={{ p: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2, width: '100%' }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <FlagIcon color="primary" />
            <Typography variant="h6" fontWeight={700}>Seller Goals</Typography>
          </Stack>
          <InfoIcon sx={{ cursor: 'pointer' }} onClick={() => setSidecarSection('goals')} />
        </Stack>
        <Divider sx={{ mb: 2 }} />
        <Stack spacing={2}>
          {(values.goals || []).map((goal, idx) => (
            <Box key={idx} sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <InlineEditField
                  label={`Goal ${idx + 1}`}
                  value={goal}
                  onSave={val => handleGoalChange(idx, val)}
                  alwaysEditing={editable}
                  multiline
                  required
                  error={!!errors.goals}
                  helperText={errors.goals}
                />
              </Box>
              <Button
                variant="outlined"
                color="error"
                size="small"
                sx={{ ml: 2 }}
                onClick={() => {
                  const newGoals = (values.goals || []).filter((_, i) => i !== idx);
                  onChange({ ...values, goals: newGoals });
                }}
                disabled={(values.goals || []).length <= 1}
              >
                Remove
              </Button>
            </Box>
          ))}
          <Button variant="outlined" onClick={handleAddGoal}>
            Add Goal
          </Button>
        </Stack>
      </Paper>

      {/* Persona Details */}
      <Paper elevation={2} sx={{ p: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2, width: '100%' }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <HailIcon color="primary" />
            <Typography variant="h6" fontWeight={700}>Persona Details</Typography>
          </Stack>
          <InfoIcon sx={{ cursor: 'pointer' }} onClick={() => setSidecarSection('persona')} />
        </Stack>
        <Divider sx={{ mb: 2 }} />
        <InlineEditField
          label="Persona Name"
          value={values.personaName}
          onSave={val => handleFieldChange('personaName', val)}
          alwaysEditing={editable}
          required
          error={!!errors.personaName}
          helperText={errors.personaName}
        />
        <InlineEditField
          label="Persona Role"
          value={values.personaRole}
          onSave={val => handleFieldChange('personaRole', val)}
          alwaysEditing={editable}
          required
          error={!!errors.personaRole}
          helperText={errors.personaRole}
        />
        <InlineEditField
          label="Persona Behaviors"
          value={values.personaBehaviors}
          onSave={val => handleFieldChange('personaBehaviors', val)}
          alwaysEditing={editable}
          required
          multiline
          error={!!errors.personaBehaviors}
          helperText={errors.personaBehaviors}
        />
        <InlineEditField
          label="Persona Bio"
          value={values.personaBio}
          onSave={val => handleFieldChange('personaBio', val)}
          alwaysEditing={editable}
          required
          multiline
          error={!!errors.personaBio}
          helperText={errors.personaBio}
        />
        <InlineEditField
          label="Voice"
          value={values.voiceName}
          onSave={shortName => {
            const selectedVoice = voiceOptions.find(v => v.ShortName === shortName);
            if (selectedVoice) {
              onChange({
                ...values,
                voiceName: selectedVoice.ShortName,
                voiceGender: selectedVoice.Gender,
              });
            } else {
              onChange({
                ...values,
                voiceName: '',
                voiceGender: '',
              });
            }
          }}
          alwaysEditing={editable}
          required
          select
          loading={loadingVoices}
          options={[
            { value: '', label: 'Select voice' },
            ...voiceOptions.map(voice => ({
              value: voice.ShortName,
              label: `${voice.DisplayName || voice.ShortName} - ${voice.Gender}`,
            })),
          ]}
          error={!!errors.voiceName}
          helperText={errors.voiceName}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            onClick={handleTestVoice}
            disabled={!values.voiceName || testingVoice}
          >
            {testingVoice ? (
              <>
                <CircularProgress size={18} sx={{ mr: 1 }} />
                Testing...
              </>
            ) : (
              'Test Voice'
            )}
          </Button>
          {!moreVoicesLoaded && (
            <Button onClick={loadMoreVoices} sx={{ ml: 1 }} size="small" variant="outlined">
              Load more voices
            </Button>
          )}
        </Box>
      </Paper>

      {/* Customer Details */}
      <Paper elevation={2} sx={{ p: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2, width: '100%' }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <BusinessIcon color="primary" />
            <Typography variant="h6" fontWeight={700}>Customer Details</Typography>
          </Stack>
          <InfoIcon sx={{ cursor: 'pointer' }} onClick={() => setSidecarSection('customer')} />
        </Stack>
        <Divider sx={{ mb: 2 }} />
        <InlineEditField
          label="Company Name"
          value={values.companyName}
          onSave={val => handleFieldChange('companyName', val)}
          alwaysEditing={editable}
          required
          error={!!errors.companyName}
          helperText={errors.companyName}
        />
        <InlineEditField
          label="Industry"
          value={values.companyIndustry}
          onSave={val => handleFieldChange('companyIndustry', val)}
          alwaysEditing={editable}
          required
          error={!!errors.companyIndustry}
          helperText={errors.companyIndustry}
        />
        <InlineEditField
          label="Company Size"
          value={values.companySize}
          onSave={val => handleFieldChange('companySize', val)}
          alwaysEditing={editable}
          required
          error={!!errors.companySize}
          helperText={errors.companySize}
        />
        <InlineEditField
          label="Locations"
          value={values.companyLocations}
          onSave={val => handleFieldChange('companyLocations', val)}
          alwaysEditing={editable}
          required
          error={!!errors.companyLocations}
          helperText={errors.companyLocations}
        />
        <InlineEditField
          label="Current Tech Profile"
          value={values.companyTechProfile}
          onSave={val => handleFieldChange('companyTechProfile', val)}
          alwaysEditing={editable}
          required
          multiline
          error={!!errors.companyTechProfile}
          helperText={errors.companyTechProfile}
        />
        <InlineEditField
          label="Recent Events / Challenges"
          value={values.companyChallenges}
          onSave={val => handleFieldChange('companyChallenges', val)}
          alwaysEditing={editable}
          required
          multiline
          error={!!errors.companyChallenges}
          helperText={errors.companyChallenges}
        />
      </Paper>

      {/* Scenario Details */}
      <Paper elevation={2} sx={{ p: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2, width: '100%' }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <ArticleIcon color="primary" />
            <Typography variant="h6" fontWeight={700}>Scenario Details</Typography>
          </Stack>
          <InfoIcon sx={{ cursor: 'pointer' }} onClick={() => setSidecarSection('scenario')} />
        </Stack>
        <Divider sx={{ mb: 2 }} />
        <InlineEditField
          label="Meeting Type"
          value={values.scenarioMeetingType}
          onSave={val => handleFieldChange('scenarioMeetingType', val)}
          alwaysEditing={editable}
          required
          error={!!errors.scenarioMeetingType}
          helperText={errors.scenarioMeetingType}
        />
        <InlineEditField
          label="Customer Type"
          value={values.scenarioCustomerType}
          onSave={val => handleFieldChange('scenarioCustomerType', val)}
          alwaysEditing={editable}
          required
          error={!!errors.scenarioCustomerType}
          helperText={errors.scenarioCustomerType}
        />
        <InlineEditField
          label="Relevant History"
          value={values.scenarioHistory}
          onSave={val => handleFieldChange('scenarioHistory', val)}
          alwaysEditing={editable}
          required
          multiline
          error={!!errors.scenarioHistory}
          helperText={errors.scenarioHistory}
        />
        <InlineEditField
          label="Key Initiatives"
          value={values.scenarioInitiatives}
          onSave={val => handleFieldChange('scenarioInitiatives', val)}
          alwaysEditing={editable}
          required
          multiline
          error={!!errors.scenarioInitiatives}
          helperText={errors.scenarioInitiatives}
        />
        <InlineEditField
          label="Painpoints / Challenges"
          value={values.scenarioChallenges}
          onSave={val => handleFieldChange('scenarioChallenges', val)}
          alwaysEditing={editable}
          required
          multiline
          error={!!errors.scenarioChallenges}
          helperText={errors.scenarioChallenges}
        />
        <InlineEditField
          label="Special Constraints / Triggers"
          value={values.scenarioConstraints}
          onSave={val => handleFieldChange('scenarioConstraints', val)}
          alwaysEditing={editable}
          required
          multiline
          error={!!errors.scenarioConstraints}
          helperText={errors.scenarioConstraints}
        />
      </Paper>

      <Box sx={{paddingBottom: '2%'}}></Box>

      <Drawer
        anchor="right"
        open={!!sidecarSection || !!sidecarSectionField}
        onClose={() => {
          setSidecarSection(null);
          setSidecarSectionField(null);
        }}
      >
        <Box sx={{ width: 350, p: 2 }}>
          <IconButton
            sx={{ float: 'right' }}
            onClick={() => {
              setSidecarSection(null);
              setSidecarSectionField(null);
            }}
          >
            <CloseIcon />
          </IconButton>
          <Typography variant="h6" gutterBottom>
            {(sidecarSectionField && sidecarSectionField[0]) ||
              (sidecarSection && {
                roleplay: "Roleplay Details",
                goals: "Seller Goals",
                persona: "Persona Details",
                customer: "Customer Details",
                scenario: "Scenario Details"
              }[sidecarSection])}
          </Typography>
          {!sidecarSectionField && sidecarSection && (
            <Box>
              {Object.entries(sectionFieldExplanations[sidecarSection]).map(([field, explanation]) => (
                <Box key={field} sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">{camelToTitle(field)}</Typography>
                  <Typography variant="body2">{explanation}</Typography>
                </Box>
              ))}
            </Box>
          )}
          {sidecarSectionField && (
            <Button size="small" onClick={() => setSidecarSectionField(null)}>
              Back to Section
            </Button>
          )}
        </Box>
      </Drawer>
    </Stack>
    
  );
};

export default RoleplayDetailsTab;